import { I18n } from '@calendly/i18n'

import { tabTitleWithBadge } from './helpers'

/**
 * Admin Login settings — same tab strip for full page and settings modal header.
 * @param {boolean} joinAnOrgEnabled — `useVariant('obi_home_new_join_an_org') === 'on'`
 */
export function getAdminLoginTabs(joinAnOrgEnabled) {
  const tabs = [
    { title: I18n.t('publisher.admin.login.tabs.sso'), route: '/app/admin/login/sso' },
    { title: I18n.t('publisher.admin.login.tabs.user_provisioning'), route: '/app/admin/login/user_provisioning' },
  ]
  if (joinAnOrgEnabled) {
    tabs.push({
      title: I18n.t('publisher.admin.login.tabs.user_access'),
      route: '/app/admin/login/user_access',
    })
  } else {
    tabs.push({
      title: I18n.t('publisher.admin.login.tabs.domain_control'),
      route: '/app/admin/login/domain_control',
    })
  }
  return tabs
}

/**
 * Admin Access in the settings modal (`/app/admin/access/*`) — same policy-aware tabs as
 * {@link features/settings_modal/settings_modal_access_page.SettingsModalAccessPage}.
 */
export function getAdminAccessModalTabs({
  base,
  joinAnOrgEnabled,
  canManageSso,
  canManageUserProvisioning,
  canManageUserAccess,
  canManageDomains,
  canManageOrganizationIntegrationSettings,
  integrationsSettingsEnabled,
}) {
  const tabs = []
  if (joinAnOrgEnabled && canManageUserAccess) {
    tabs.push({
      title: I18n.t('publisher.admin.login.tabs.user_access'),
      route: `${base}/user_access`,
    })
  } else if (!joinAnOrgEnabled && canManageDomains) {
    tabs.push({
      title: I18n.t('publisher.admin.login.tabs.domain_control'),
      route: `${base}/domain_control`,
    })
  }
  if (canManageSso) {
    tabs.push({
      title: I18n.t('publisher.admin.login.tabs.sso'),
      route: `${base}/sso`,
    })
  }
  if (canManageUserProvisioning) {
    tabs.push({
      title: I18n.t('publisher.admin.login.tabs.user_provisioning'),
      route: `${base}/user_provisioning`,
    })
  }
  tabs.push({
    title: I18n.t('publisher.admin.security.tabs.activity_log'),
    route: `${base}/activity_log`,
  })
  if (integrationsSettingsEnabled && canManageOrganizationIntegrationSettings) {
    tabs.push({
      title: I18n.t('publisher.admin.login.tabs.integrations'),
      route: `${base}/integrations`,
    })
  }
  return tabs
}

/**
 * Admin Security when embedded in the settings modal — subset of full Security routes.
 * @param {boolean} bookingVerificationSettingsEnabled — `trust_and_safety_email_verification_feature`
 */
export function getAdminSecurityModalTabs(bookingVerificationSettingsEnabled) {
  return [
    { title: I18n.t('publisher.admin.security.modal_tabs.data'), route: '/app/admin/security/data_deletion' },
    ...(bookingVerificationSettingsEnabled
      ? [{ title: I18n.t('publisher.admin.security.modal_tabs.privacy'), route: '/app/admin/security/booking' }]
      : []),
  ]
}

/**
 * Admin Security full page (`/app/admin/security/*`) — tab strip for standalone routing (not settings modal).
 * @param {object} opts
 * @param {boolean} opts.bookingVerificationSettingsEnabled — `trust_and_safety_email_verification_feature`
 * @param {boolean} opts.joinOrgExperiment — `obi_home_join_an_org_experiment`
 */
export function getAdminSecurityFullPageTabs({ bookingVerificationSettingsEnabled, joinOrgExperiment }) {
  return [
    { title: I18n.t('publisher.admin.security.tabs.data_deletion'), route: '/app/admin/security/data_deletion' },
    { title: I18n.t('publisher.admin.security.tabs.activity_log'), route: '/app/admin/security/activity_log' },
    ...(bookingVerificationSettingsEnabled
      ? [{ title: I18n.t('publisher.admin.security.tabs.booking'), route: '/app/admin/security/booking' }]
      : []),
    ...(joinOrgExperiment
      ? [{ title: I18n.t('publisher.organization_settings.visibility.title'), route: '/app/admin/security/visibility' }]
      : []),
  ]
}

/**
 * Admin Permissions — same tab strip for full page and settings modal header.
 * @param {boolean} isNewBadgeHidden — `remove_new_flags_comp`
 * @param {{ showPayments?: boolean }} [options]
 */
export function getAdminPermissionsTabs(isNewBadgeHidden, { showPayments } = {}) {
  const tabs = [
    { title: I18n.t('publisher.admin.permissions.tabs.event_types'), route: '/app/admin/permissions/event_types' },
    { title: I18n.t('publisher.admin.permissions.tabs.workflows'), route: '/app/admin/permissions/workflows' },
    {
      title: tabTitleWithBadge(I18n.t('publisher.admin.permissions.tabs.invitations'), 'new', isNewBadgeHidden),
      route: '/app/admin/permissions/invitations',
    },
  ]
  if (showPayments) {
    tabs.push({
      title: I18n.t('publisher.admin.permissions.tabs.payments'),
      route: '/app/admin/permissions/payments',
    })
  }
  return tabs
}
